import { useCallback, useRef, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import type { PanelMenuItem } from "@/data/panels";
import BottomSheet from "@/components/base/BottomSheet";

interface RoleBottomNavProps {
  menu: PanelMenuItem[];
  bottomNav: PanelMenuItem[];
  homePath: string;
  roleLabel: string;
}

export default function RoleBottomNav({
  menu,
  bottomNav,
  homePath,
  roleLabel,
}: RoleBottomNavProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const sheetId = `${roleLabel.toLocaleLowerCase("tr-TR")}-menu-sheet`;
  const navigate = useNavigate();

  const goTop = () => {
    window.scrollTo(0, 0);
  };

  const handleNav = (href: string) => {
    navigate(href);
    goTop();
    // Mobilde bottom sheet kapanmadan önce navigasyonun tamamlanması için
    // kısa bir gecikme eklenir (linkin unmount'u dokunmatik tıklamayı kesmemesi için).
    setTimeout(() => close(), 120);
  };

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-primary-950 border-t border-white/10"
        aria-label={`${roleLabel} mobil menüsü`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch h-16 max-w-md mx-auto">
          {bottomNav.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === homePath}
              className={({ isActive }) =>
                `relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 transition-colors duration-200 ${
                  isActive ? "text-accent-300" : "text-white/60 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <span className="text-[11px] font-medium leading-none whitespace-nowrap px-1 max-w-full truncate">
                    {item.label}
                  </span>
                  <span
                    className={`absolute top-0 inset-x-3 h-0.5 rounded-full bg-accent-400 origin-center transition-transform duration-200 ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                    aria-hidden="true"
                  ></span>
                </>
              )}
            </NavLink>
          ))}

          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-controls={sheetId}
            className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 text-white/60 hover:text-white transition-colors duration-200 cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-menu-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="text-[11px] font-medium leading-none whitespace-nowrap">Tümü</span>
          </button>
        </div>
      </nav>

      <BottomSheet
        open={open}
        onClose={close}
        title="Tüm menü"
        titleId={`${roleLabel.toLocaleLowerCase("tr-TR")}-menu-title`}
        id={sheetId}
        returnFocusRef={triggerRef}
      >
        <nav
          aria-label={`${roleLabel} paneli tüm menü`}
          className="flex flex-col gap-0.5"
        >
          {menu.map((item) => (
            <button
              key={item.href}
              type="button"
              onClick={() => handleNav(item.href)}
              className="flex items-center gap-3 px-3 py-3 -mx-3 rounded-md text-[15px] font-medium text-foreground-800 hover:bg-background-100 hover:text-primary-700 transition-colors text-left"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-base text-foreground-400`} aria-hidden="true"></i>
              </span>
              {item.label}
            </button>
          ))}
        </nav>
      </BottomSheet>
    </>
  );
}